/**
 * Jack & Jones scroll-snap carousel.
 *
 * Markup: [data-jj-carousel] > [data-jj-track] (the horizontal scroller, one child per slide),
 * optional [data-jj-prev] / [data-jj-next] buttons and [data-jj-dot] indicators (buttons are clickable).
 * Arrows get `disabled` when there is nowhere to go, and CSS dims them (jj-base.css).
 * With data-jj-duration="<ms>" on the root, arrows and dots move the rail with an ease-out of that
 * length instead of the browser's smooth scroll, whose length the browser picks (~330 ms).
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * CSS `ease-out`, cubic-bezier(0, 0, 0.58, 1): progress at time t (0–1), solved by bisection.
 * @param {number} t
 */
function easeOut(t) {
  const x1 = 0;
  const x2 = 0.58;
  /** @param {number} u @param {number} p1 @param {number} p2 */
  const bezier = (u, p1, p2) => 3 * (1 - u) * (1 - u) * u * p1 + 3 * (1 - u) * u * u * p2 + u * u * u;
  let low = 0;
  let high = 1;
  let u = t;
  for (let i = 0; i < 20; i++) {
    u = (low + high) / 2;
    if (bezier(u, x1, x2) < t) low = u;
    else high = u;
  }
  return bezier(u, 0, 1);
}
class JJCarousel {
  /** @param {HTMLElement} root */
  constructor(root) {
    this.root = root;
    this.track = /** @type {HTMLElement} */ (root.querySelector('[data-jj-track]'));
    this.prev = root.querySelector('[data-jj-prev]');
    this.next = root.querySelector('[data-jj-next]');
    this.dots = Array.from(root.querySelectorAll('[data-jj-dot]'));
    this.duration = Number(root.dataset.jjDuration) || 0;
    this.frame = 0;
    /** @type {number | null} */
    this.target = null;
    if (!this.track) return;

    // A finger or the wheel on the rail takes over from the animation
    if (this.duration) {
      for (const type of ['touchstart', 'wheel']) {
        this.track.addEventListener(type, () => this.stop(), { passive: true });
      }
    }

    this.prev?.addEventListener('click', () => this.go(-1));
    this.next?.addEventListener('click', () => this.go(1));
    this.dots.forEach((dot, index) => {
      if (dot instanceof HTMLButtonElement) dot.addEventListener('click', () => this.goTo(index));
    });

    let frame = 0;
    this.track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.update());
      },
      { passive: true }
    );
    new ResizeObserver(() => this.update()).observe(this.track);
    this.update();
  }

  get slides() {
    return /** @type {HTMLElement[]} */ (Array.from(this.track.children));
  }

  get step() {
    const [first, second] = this.slides;
    if (!first) return this.track.clientWidth;
    return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
  }

  /** @param {number} direction */
  go(direction) {
    if (this.duration) {
      // A click while it moves counts from where the rail is going, like a native scrollBy
      this.scrollTo((this.target ?? this.track.scrollLeft) + direction * this.step);
      return;
    }
    this.track.scrollBy({ left: direction * this.step, behavior: 'smooth' });
  }

  /** @param {number} index */
  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    const left = slide.offsetLeft - this.slides[0].offsetLeft;
    if (this.duration) this.scrollTo(left);
    else this.track.scrollTo({ left, behavior: 'smooth' });
  }

  stop() {
    if (this.target === null) return;
    cancelAnimationFrame(this.frame);
    this.target = null;
    this.track.style.scrollSnapType = '';
  }

  /**
   * Moves the rail to `left` in `duration` ms with ease-out. Snap is off meanwhile (with x mandatory
   * every frame's scrollLeft would snap) and comes back at the end, already on a snap point.
   * @param {number} left
   */
  scrollTo(left) {
    const { track } = this;
    const max = track.scrollWidth - track.clientWidth;
    const to = Math.max(0, Math.min(max, left));
    cancelAnimationFrame(this.frame);
    if (REDUCED_MOTION.matches) {
      this.target = null;
      track.scrollLeft = to;
      return;
    }

    const from = track.scrollLeft;
    const start = performance.now();
    this.target = to;
    track.style.scrollSnapType = 'none';
    /** @param {number} now */
    const step = (now) => {
      const t = Math.min(1, (now - start) / this.duration);
      track.scrollLeft = from + (to - from) * easeOut(t);
      if (t < 1) {
        this.frame = requestAnimationFrame(step);
        return;
      }
      this.target = null;
      track.style.scrollSnapType = '';
    };
    this.frame = requestAnimationFrame(step);
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const start = Math.abs(scrollLeft) <= 1;
    const end = Math.abs(scrollLeft) + clientWidth >= scrollWidth - 1;

    if (this.prev) this.prev.toggleAttribute('disabled', start);
    if (this.next) this.next.toggleAttribute('disabled', end);

    if (this.dots.length) {
      const index = end ? this.dots.length - 1 : Math.round(Math.abs(scrollLeft) / this.step);
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    }
  }
}

/**
 * Photo slider inside a product card: [data-jj-card-slider] > [data-jj-card-slides] and buttons with
 * data-jj-card-step="-1|1". The active image carries .is-active and CSS dissolves it in. It loops, so
 * its arrows never need a disabled state (rule C2). With a mouse, hovering the card shows the 2nd
 * image; once the cursor moves, the photo is split in equal zones (left to right) and the image of the
 * zone under the cursor is shown, with the progress bar filled up to it; leaving the card goes back
 * to the 1st (Figma 1005:84602). The arrows win over the zones: moves over an arrow don't pick an
 * image, and after a click on one the zones stay off until the cursor leaves the card, so each click
 * steps from the photo it shows. On the JUNIOR carousel (.jj-pcarousel--landing, Figma 1149:8622
 * Hover: Foto 2, sin zonas) and with «Al pasar el mouse, sólo la 2.ª foto» (.jj-pcarousel--hover-2nd,
 * the home, Figma 1149:8644) there are no zones: hovering just shows the 2nd image. With touch, a
 * horizontal swipe on the photo steps it.
 */

/**
 * @param {Element} card
 * @param {number} index
 */
function setCardSlide(card, index) {
  const slides = card.querySelector('[data-jj-card-slides]');
  if (!(slides instanceof HTMLElement)) return;
  const count = slides.children.length;
  const next = ((index % count) + count) % count;
  Array.from(slides.children).forEach((image, i) => image.classList.toggle('is-active', i === next));
  slides.dataset.jjSlide = String(next);
  if (card instanceof HTMLElement) card.style.setProperty('--jj-card-slide', String(next));
}

/** @param {MouseEvent} event */
function onCardStep(event) {
  const button = event.target instanceof Element ? event.target.closest('[data-jj-card-step]') : null;
  if (!button) return;
  const card = button.closest('[data-jj-card-slider]');
  const slides = card?.querySelector('[data-jj-card-slides]');
  if (!card || !(slides instanceof HTMLElement)) return;

  const current = Number(slides.dataset.jjSlide) || 0;
  // Zones off until the cursor leaves the card (onCardHover clears it on the way out)
  if (card instanceof HTMLElement && card.dataset.jjHoverX !== undefined) card.dataset.jjHoverX = 'stepped';
  setCardSlide(card, current + Number(button.getAttribute('data-jj-card-step')));
}

/**
 * Hover in and out of a card (mouse only): mouseover/mouseout bubble, so one listener serves every
 * card, also the ones a section reload adds.
 * @param {MouseEvent} event
 */
function onCardHover(event) {
  if (!hoverQuery.matches) return;
  const card = event.target instanceof Element ? event.target.closest('[data-jj-card-slider]') : null;
  if (!(card instanceof HTMLElement) || (event.relatedTarget instanceof Node && card.contains(event.relatedTarget))) {
    return;
  }
  const entering = event.type === 'mouseover';
  if (entering) card.dataset.jjHoverX = String(event.clientX);
  else delete card.dataset.jjHoverX;
  setCardSlide(card, entering ? 1 : 0);
}

/** Cursor moved more than this from where it entered: from then on the zone picks the image */
const HOVER_DEAD_ZONE = 8;

/** @param {MouseEvent} event */
function onCardMove(event) {
  if (!hoverQuery.matches) return;
  const card = event.target instanceof Element ? event.target.closest('[data-jj-card-slider]') : null;
  if (!(card instanceof HTMLElement) || card.dataset.jjHoverX === undefined) return;
  if (card.closest('.jj-pcarousel--landing, .jj-pcarousel--hover-2nd')) return;
  if (card.dataset.jjHoverX === 'stepped' || event.target.closest('[data-jj-card-step]')) return;
  if (card.dataset.jjHoverX !== 'moved') {
    if (Math.abs(event.clientX - Number(card.dataset.jjHoverX)) < HOVER_DEAD_ZONE) return;
    card.dataset.jjHoverX = 'moved';
  }

  const media = card.querySelector('[data-jj-card-media], .jj-card__media');
  const slides = card.querySelector('[data-jj-card-slides]');
  if (!media || !(slides instanceof HTMLElement)) return;
  const rect = media.getBoundingClientRect();
  if (event.clientY < rect.top || event.clientY > rect.bottom || !rect.width) return;

  const count = slides.children.length;
  const zone = Math.min(count - 1, Math.max(0, Math.floor(((event.clientX - rect.left) / rect.width) * count)));
  if (zone !== Number(slides.dataset.jjSlide)) setCardSlide(card, zone);
}

/** Horizontal travel of a finger on the photo that counts as a swipe */
const SWIPE_THRESHOLD = 40;

/** @type {{ card: HTMLElement, x: number, y: number } | null} */
let cardSwipe = null;

/**
 * Touch: without arrows (mobile), a horizontal swipe on the photo moves to the previous or next image.
 * @param {TouchEvent} event
 */
function onCardTouchStart(event) {
  cardSwipe = null;
  const card = event.target instanceof Element ? event.target.closest('[data-jj-card-slider]') : null;
  const media = card?.querySelector('[data-jj-card-media]');
  const [touch] = event.touches;
  if (!(card instanceof HTMLElement) || !media || !touch || event.touches.length > 1) return;
  const rect = media.getBoundingClientRect();
  if (touch.clientY < rect.top || touch.clientY > rect.bottom) return;
  cardSwipe = { card, x: touch.clientX, y: touch.clientY };
}

/** @param {TouchEvent} event */
function onCardTouchEnd(event) {
  const swipe = cardSwipe;
  cardSwipe = null;
  const [touch] = event.changedTouches;
  if (!swipe || !touch) return;
  const dx = touch.clientX - swipe.x;
  if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(touch.clientY - swipe.y)) return;

  const slides = swipe.card.querySelector('[data-jj-card-slides]');
  if (!(slides instanceof HTMLElement)) return;
  setCardSlide(swipe.card, (Number(slides.dataset.jjSlide) || 0) + (dx < 0 ? 1 : -1));
}

const hoverQuery = window.matchMedia('(hover: hover)');

if (!window.jjCardSliderReady) {
  window.jjCardSliderReady = true;
  document.addEventListener('click', onCardStep);
  document.addEventListener('mouseover', onCardHover);
  document.addEventListener('mouseout', onCardHover);
  document.addEventListener('mousemove', onCardMove, { passive: true });
  document.addEventListener('touchstart', onCardTouchStart, { passive: true });
  document.addEventListener('touchend', onCardTouchEnd, { passive: true });
  document.addEventListener('touchcancel', () => (cardSwipe = null), { passive: true });
}

function init() {
  document.querySelectorAll('[data-jj-carousel]').forEach((root) => {
    const element = /** @type {HTMLElement} */ (root);
    if (element.dataset.jjReady) return;
    element.dataset.jjReady = 'true';
    new JJCarousel(element);
  });
}

init();
document.addEventListener('shopify:section:load', init);

/**
 * <jj-carousel>: the same carousel for markup that arrives after load (the typed results of the
 * search panel, which Horizon morphs in). It starts when connected, and the arrows follow when morph
 * swaps the slides of a carousel that stays in the page.
 */
if (!customElements.get('jj-carousel')) {
  customElements.define(
    'jj-carousel',
    class extends HTMLElement {
      connectedCallback() {
        if (this.carousel) return;
        /** @type {JJCarousel} */
        this.carousel = new JJCarousel(this);
        const { track } = this.carousel;
        if (track) new MutationObserver(() => this.carousel?.update()).observe(track, { childList: true });
      }
    }
  );
}
